/**
 * Authenticated UP-LAB API transport and shared browser-safe DTOs.
 * Owns session headers and HTTP errors, never interprets feature-specific failure causes.
 */

import type {
  AttachmentBackfillInputDto,
  AttachmentBackfillResult,
  AttachmentInventory,
  AttachmentInventoryQueryDto,
  AttachmentInventoryStats,
  AttachmentReferenceStatus,
  AttachmentReviewInputDto,
} from '../../../shared/contracts/attachment-admin.ts';
import type {
  GitlabAnalyticsQuery,
  GitlabAnalyticsResult,
} from '../../../shared/contracts/gitlab-analytics.ts';
import type {
  IdentityView,
  InstanceCredentialView,
} from '../../../shared/contracts/gitlab-identity.ts';
import type { GitlabProject } from '../../../shared/contracts/gitlab-projects.ts';
import type { GitlabUsersPage, GitlabUsersQuery } from '../../../shared/contracts/gitlab-users.ts';
import type {
  TosConfigInputDto,
  TosConfigView,
  TosConnectionResult,
} from '../../../shared/contracts/tos.ts';

export interface GitlabIdentityCandidate {
  readonly id: number;
  readonly username: string;
  readonly name: string;
  readonly state: string;
  readonly bot: boolean;
}

export interface MetricValue {
  readonly id: string;
  readonly label: string;
  readonly value: number | null;
  readonly unit: string;
  readonly threshold: number | null;
  readonly status: 'ok' | 'warn' | 'unknown';
  readonly direction: 'higher_better' | 'lower_better' | 'neutral';
  readonly counter: readonly string[];
  readonly counterLabels: readonly string[];
  readonly detail: Record<string, unknown> | null;
  /** 近 12 期趋势值（board 顺带带回来的 sparkline 数据），取不到时为 null。 */
  readonly spark: readonly (number | null)[] | null;
}

export interface MetricDef {
  readonly id: string;
  readonly label: string;
  readonly definition: string;
  readonly formula: string;
  readonly unit: string;
  readonly direction: string;
  readonly counter: readonly string[];
  readonly headline: boolean;
  readonly observational: boolean;
  readonly threshold: number | null;
  readonly source: string;
}

export interface Board {
  readonly period: { readonly hours: number };
  readonly headline: readonly MetricValue[];
  readonly others?: readonly MetricValue[];
  readonly warnings: readonly string[];
}

export interface TrendPoint {
  readonly periodIndex: number;
  readonly value: number | null;
  readonly status: string;
}

export interface Trend {
  readonly metric: string;
  readonly label: string;
  readonly unit: string;
  readonly direction: string;
  readonly points: readonly TrendPoint[];
  readonly delta: number | null;
  readonly improving: boolean | null;
}

export interface Decision {
  readonly id: string;
  readonly kind: string;
  readonly title: string;
  readonly analysis: string;
  readonly options: readonly {
    readonly id: string;
    readonly label: string;
    readonly value: string;
  }[];
  readonly recommendation: string | null;
  readonly severity: 'low' | 'normal' | 'high';
  readonly hypothesisId: string | null;
  readonly createdAt: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

const TOKEN_KEY = 'uplab_token';

const token = (): string => localStorage.getItem(TOKEN_KEY) ?? '';

/** 401 时统一回调：App 在这里把会话清掉、回到登录页。 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    headers: token() === '' ? {} : { Authorization: `Bearer ${token()}` },
  });
  if (!res.ok) {
    // Keep the service's safe reason; 503 may describe any unavailable integration.
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 401) onUnauthorized?.();
    throw new ApiError(body.error ?? `HTTP ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

async function post<T>(
  path: string,
  payload: unknown,
  options?: { redirect: RequestRedirect },
): Promise<T> {
  const binary = payload instanceof Blob;
  const res = await fetch(path, {
    method: 'POST',
    ...options,
    headers: {
      'content-type': binary ? 'application/zip' : 'application/json',
      ...(token() === '' ? {} : { Authorization: `Bearer ${token()}` }),
    },
    body: binary ? payload : JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.();
    throw new ApiError(body.error ?? `HTTP ${res.status}`, res.status);
  }
  return body as T;
}

/** Narrow authenticated transport for lazy feature clients; keeps token ownership here. */
export const apiTransport = { get, post };

export interface AuthUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  /** 角色名（RBAC，app_role.name）。 */
  readonly role: string;
  /** 该角色的权限点集合，登录/注册时由后端算好。 */
  readonly permissions: readonly string[];
  /** 为 true 时系统锁死在改密页，改完才能用（服务端强制，前端只负责渲染）。 */
  readonly mustChangePassword: boolean;
  /** SSO 统一身份落地账号：密码在 IdP 侧管理，本系统不提供改密入口。 */
  readonly isSso: boolean;
}

export interface AuthResult {
  readonly token: string;
  readonly expiresAt: string;
  readonly user: AuthUser;
}

/** 会话列表行（设备管理页）。tokenFingerprint 是 SHA256 前 8 位，非完整摘要。 */
export interface SessionRow {
  readonly tokenFingerprint: string;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly createdIp: string | null;
  readonly userAgent: string | null;
}

export interface SessionList {
  readonly sessions: readonly SessionRow[];
  /** 当前令牌的指纹：与行内 tokenFingerprint 相同者即「本设备」。 */
  readonly currentFingerprint: string;
}

export type LoginLogEventKind =
  | 'login'
  | 'logout'
  | 'sso_login'
  | 'session_revoked'
  | 'password_changed'
  | 'account_disabled';

export interface LoginLogEntry {
  readonly id: string;
  readonly event: LoginLogEventKind;
  readonly method: 'password' | 'sso' | 'desktop' | null;
  readonly ip: string | null;
  readonly userAgent: string | null;
  readonly createdAt: string;
}

export interface LoginLogPage {
  readonly entries: readonly LoginLogEntry[];
}

// ── 管理接口（RBAC）────────────────────────────────────────

export interface ManagedUser {
  readonly id: string;
  readonly username: string;
  readonly displayName: string;
  readonly role: string;
  readonly status: string;
  readonly createdAt: string;
  readonly permissions: readonly string[];
}

export interface RoleInfo {
  readonly name: string;
  readonly description: string;
}

export interface RoleRow {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly isSystem: boolean;
  readonly permissions: readonly string[];
  readonly userCount: number;
}

export interface PermissionDef {
  readonly key: string;
  readonly label: string;
  readonly description: string;
}

// ── 归因与进化（attribution:read）──────────────────────────

export interface TaskBrief {
  readonly id: string;
  readonly seq: number;
  readonly goal: string;
  readonly status: string;
  readonly assignee: string | null;
  readonly clientScope: string;
}

export interface HypothesisNode {
  readonly id: string;
  readonly level: string;
  readonly statement: string;
  readonly expectedOutcome: string | null;
  readonly falsifyCondition: string | null;
  readonly verifyBy: string | null;
  readonly valueWeight: number;
  readonly status: string;
  readonly owner: string;
  readonly createdAt: string;
  readonly support: number;
  readonly against: number;
  readonly tasks: readonly TaskBrief[];
}

export interface AssetItem {
  readonly id: string;
  readonly kind: string;
  readonly title: string;
  readonly summary: string;
  readonly location: string;
  readonly clientScope: string;
  readonly industry: string | null;
  readonly reuseCount: number;
  readonly status: string;
  readonly tags: readonly string[];
}

export interface ReuseStats {
  readonly total: number;
  readonly active: number;
  readonly reused: number;
  readonly reuseRate: number | null;
  readonly generalizationRate: number | null;
}

export interface Attribution {
  readonly hypotheses: readonly HypothesisNode[];
  readonly assets: readonly AssetItem[];
  readonly reuse: ReuseStats;
}

export interface EvidenceItem {
  readonly id: string;
  readonly polarity: string;
  readonly weight: number;
  readonly note: string | null;
  readonly createdAt: string;
  readonly taskSeq: number | null;
}

export interface CostBreakdown {
  readonly tokens: {
    readonly context: number;
    readonly exec: number;
    readonly rework: number;
    readonly explore: number;
  };
  readonly hours: { readonly decide: number; readonly verify: number; readonly fix: number };
  readonly tokenCostFen: number;
  readonly humanCostFen: number;
  readonly passedFirstTry: boolean;
  readonly reworkCount: number;
}

export interface TaskDetail {
  readonly id: string;
  readonly seq: number;
  readonly goal: string;
  readonly status: string;
  readonly assignee: string | null;
  readonly clientScope: string;
  readonly attemptCount: number;
  readonly reworkCount: number;
  readonly startedAt: string | null;
  readonly deliveredAt: string | null;
  readonly eval: {
    readonly name: string;
    readonly kind: string;
    readonly threshold: string | null;
  } | null;
  readonly cost: CostBreakdown | null;
}

export interface HypothesisDetail {
  readonly hypothesis: {
    readonly id: string;
    readonly level: string;
    readonly statement: string;
    readonly expectedOutcome: string | null;
    readonly falsifyCondition: string | null;
    readonly verifyBy: string | null;
    readonly valueWeight: number;
    readonly status: string;
    readonly owner: string;
    readonly createdAt: string;
  };
  readonly evidence: {
    readonly support: number;
    readonly against: number;
    readonly strength: number | null;
    readonly items: readonly EvidenceItem[];
  };
  readonly tasks: readonly TaskDetail[];
}

export interface GlobalTaskCost {
  readonly tokens: {
    readonly context: number;
    readonly exec: number;
    readonly rework: number;
    readonly explore: number;
  };
  readonly totalTokens: number | null;
  readonly reworkRatio: number | null;
  readonly hours: { readonly decide: number; readonly verify: number; readonly fix: number };
  readonly tokenCostFen: number;
  readonly humanCostFen: number;
  readonly passedFirstTry: boolean;
  readonly reworkCount: number;
}

export interface GlobalTask {
  readonly id: string;
  readonly seq: number;
  readonly goal: string;
  readonly status: string;
  readonly assignee: string | null;
  readonly clientScope: string;
  readonly executor: string;
  readonly attemptCount: number;
  readonly reworkCount: number;
  readonly hypothesisId: string;
  readonly hypothesisStatement: string | null;
  readonly cost: GlobalTaskCost | null;
}

export interface SignatureGroupItem {
  readonly signature: string;
  readonly kind: string;
  readonly count: number;
  readonly cause: string;
  readonly sink: string;
  readonly sinkLabel: string;
  readonly firstSeenAt: string;
  readonly lastSeenAt: string;
  readonly alreadySunk: boolean;
  readonly sample: string;
}

export interface FrictionRepeatStats {
  readonly total: number;
  readonly signatures: number;
  readonly repeats: number;
  readonly rate: number | null;
  readonly promotable: number;
  readonly sunk: number;
}

export interface EvolutionOverview {
  readonly total: number;
  readonly bySink: Record<string, number>;
  readonly byVerdict: Record<string, number>;
  readonly effective: number;
  readonly ineffective: number;
  readonly pendingVerify: number;
}

export interface Evolution {
  readonly repeat: FrictionRepeatStats;
  readonly overview: EvolutionOverview;
  readonly groups: readonly SignatureGroupItem[];
}

// ── 规则（rule:manage）─────────────────────────────────────

export interface RuleStats {
  readonly total: number;
  readonly active: number;
  readonly draft: number;
  readonly disabled: number;
  readonly overdueReview: number;
  readonly appliedLast24h: number;
  readonly escalatedLast24h: number;
}

export interface RuleItem {
  readonly id: string;
  readonly name: string;
  readonly event: string;
  readonly condition: unknown;
  readonly action: string;
  readonly params: unknown;
  readonly status: string;
  readonly version: number;
  readonly reviewBy: string | null;
  readonly owner: string;
  readonly rationale: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface RuleExecution {
  readonly at: string;
  readonly sourceEvent: string;
  readonly eventType: string;
  readonly trace: {
    readonly matched: boolean;
    readonly effect?: string;
    readonly error?: string;
    readonly ruleVersion?: number;
  };
}

export interface RuleSimulationResult {
  readonly at: string;
  readonly eventType: string;
  readonly matched: boolean;
  readonly ruleVersion: number;
}

export type ManagedRule = RuleItem & {
  readonly lastResult: RuleExecution | null;
  readonly latestPreview?: RuleSimulationResult | null;
};
export interface RulePage<T> {
  readonly items: readonly T[];
  readonly total: number;
  readonly page: number;
  readonly perPage: number;
  readonly nextPage: number | null;
}

export interface RuleMeta {
  readonly events: readonly string[];
  readonly handlers: readonly {
    name: string;
    description: string;
    paramsSchema?: { properties?: Record<string, { default?: unknown; description?: string }> };
  }[];
  readonly fields: readonly string[];
  readonly ops: readonly string[];
}

export interface SaveRuleInput {
  readonly id: string;
  readonly name: string;
  readonly event: string;
  readonly action: string;
  readonly condition: unknown;
  readonly params: unknown;
  readonly reviewBy?: string | null;
  readonly rationale?: string;
  readonly activate?: boolean;
  readonly expectedVersion: number;
}

// ── 系统设置（config:manage）───────────────────────────────

export interface ConfigField {
  readonly name: string;
  readonly label: string;
  readonly type: 'number' | 'time' | 'text';
  readonly min?: number;
  readonly max?: number;
  readonly integer?: boolean;
  readonly hint?: string;
}

/** 一个可配置项 = 注册表元数据 + 库里的当前值。 */
export interface ConfigItem {
  readonly key: string;
  readonly label: string;
  readonly group: string;
  readonly type: 'number' | 'string' | 'object';
  readonly unit: string | null;
  readonly min: number | null;
  readonly max: number | null;
  readonly integer: boolean;
  readonly effect: string;
  /** 库里没有行时的等效值（如 registration 未设置视为 on）。 */
  readonly absentValue: unknown;
  readonly fields: readonly ConfigField[] | null;
  readonly value: unknown;
  readonly isSet: boolean;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
}

/** harness 过程健康：队列水位 + 每个 job 的最近成功与缺口。 */
export interface HarnessJobStatus {
  readonly name: string;
  readonly description: string;
  readonly schedule: string;
  readonly intervalHours: number;
  readonly lastSuccess: string | null;
  readonly hoursSinceSuccess: number | null;
  readonly stale: boolean;
}

export interface HarnessHealth {
  readonly queue: {
    readonly pending: number;
    readonly processing: number;
    readonly dead: number;
    readonly oldestPendingSeconds: number | null;
  };
  readonly jobs: readonly HarnessJobStatus[];
}

/** 验收评审模型配置回显（AGENT-INTEGRATION Phase 1）。地址与模型明文，key 只给尾四位。 */
export interface EvalModelView {
  readonly exists: boolean;
  readonly baseUrl: string | null;
  readonly model: string | null;
  readonly apiKeyLast4: string | null;
  readonly updatedBy: string | null;
  readonly updatedAt: string | null;
}

/** 浏览器直连 SSO 登录的挑战（sso.ts directChallenge 的前端视图）。 */
export interface SsoDirectChallenge {
  readonly state: string;
  readonly codeChallenge: string;
  readonly casdoorBaseUrl: string;
  readonly clientId: string;
  readonly organization: string;
  readonly application: string;
}

/** SSO 配置回显。地址 / client_id / 角色明文，secret 只给尾四位。 */
export interface SsoConfigView {
  readonly exists: boolean;
  readonly baseUrl: string | null;
  readonly clientId: string | null;
  readonly ssoRole: string | null;
  readonly clientSecretLast4: string | null;
  readonly updatedBy: string | null;
  readonly updatedAt: string | null;
  /** 自绘登录（方案二）：Casdoor 应用名与组织名；两者齐备才可用账密直登。 */
  readonly casdoorApp: string;
  readonly casdoorOrg: string;
  readonly directReady: boolean;
}

/** 飞书凭据的界面回显。只有尾四位，密文与明文都不出服务端。 */
export interface FeishuCredentialsView {
  readonly exists: boolean;
  readonly appId: string | null;
  readonly appSecretLast4: string | null;
  readonly verificationTokenLast4: string | null;
  readonly encryptKeyLast4: string | null;
  readonly updatedBy: string | null;
  readonly updatedAt: string | null;
}

// ── GitLab 审计（gitlab:audit）──────────────────────────────

/** 已接入的 GitLab 凭证（查询目标选择用），不含任何令牌信息。 */
export interface GitlabAccount {
  readonly id: string;
  readonly host: string;
  readonly mode: string;
  readonly scope: string | null;
  readonly username: string | null;
  readonly status: string;
  readonly accessScope?: string;
}

export type { GitlabProject } from '../../../shared/contracts/gitlab-projects.ts';

export interface GitlabCommit {
  readonly sha: string;
  readonly shortSha: string;
  readonly title: string;
  readonly message: string;
  readonly authorName: string;
  /** Git 作者邮箱，属于可自行配置的提交元数据。 */
  readonly authorEmail: string | null;
  /** 提交作者归属到的 up-lab 账号。null = 未匹配（未接入成员）。 */
  readonly memberUsername: string | null;
  readonly authoredAt: string;
  readonly committedAt?: string | null;
  readonly changedLines: number;
  readonly stats?: {
    readonly additions: number;
    readonly deletions: number;
    readonly total: number;
  } | null;
  readonly webUrl: string;
}

export interface GitlabCommitDetail {
  readonly sha: string;
  readonly shortSha: string;
  readonly title: string;
  readonly message: string;
  readonly authorName: string;
  readonly authorEmail: string;
  readonly authoredAt: string;
  readonly committedAt: string;
  readonly parents: readonly string[];
  readonly stats: { additions: number; deletions: number; total: number } | null;
  readonly webUrl: string;
}

/** GitLab 审计列表的统一分页信封（服务端透传 GitLab 分页头）。 */
export interface GitlabPage<T> {
  /** Only project-list responses from servers that support upstream search include this. */
  readonly searchSupported?: boolean;
  readonly items: readonly T[];
  readonly page: number;
  readonly perPage: number;
  readonly nextPage: number | null;
  readonly total: number | null;
  readonly truncated?: boolean;
  readonly totalStatus?: 'exact' | 'not_requested' | 'limit_reached' | 'unavailable';
}

/** 用户自助 GitLab 凭证回显（令牌只给尾四位）。 */
export interface GitlabSelfCredentialView {
  readonly exists: boolean;
  readonly host: string | null;
  readonly gitlabUsername: string | null;
  readonly tokenLast4: string | null;
  readonly status: string | null;
  readonly createdAt: string | null;
  readonly hostManaged?: boolean;
  readonly configuredHost?: string | null;
  readonly needsReconnect?: boolean;
}

/** 用户自建的中转站连接回显。令牌只给尾四位。 */
export interface UpmoreConnView {
  readonly id: string;
  readonly label: string;
  readonly baseUrl: string;
  readonly accessTokenLast4: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** 网关令牌（/api/token/ 的字段投影）。 */
export interface UpmoreToken {
  readonly id: number;
  readonly name: string;
  readonly status: number;
  readonly usedQuota: number;
  readonly remainQuota: number;
  readonly unlimitedQuota: boolean;
  readonly group: string;
  readonly createdTime: number;
  readonly accessedTime: number;
  readonly expiredTime: number;
}

/** 时间窗用量汇总（/api/log/self/stat 投影）。 */
export interface UpmoreUsageStat {
  readonly quota: number;
  readonly rpm: number;
  readonly tpm: number;
}

/** 单次调用日志（/api/log/self 的字段投影）。 */
export interface UpmoreCallLog {
  readonly id: number;
  readonly createdAt: number;
  readonly tokenName: string;
  readonly modelName: string;
  readonly quota: number;
  readonly promptTokens: number;
  readonly completionTokens: number;
  /** 缓存读取 Token；缺失或 null 表示网关未提供。 */
  readonly cacheReadTokens?: number | null;
  readonly useTime: number;
  readonly isStream: boolean;
  readonly group: string;
}

/** 网关货币口径：quota→美元基准 + 美元→人民币汇率（/api/status 公开字段）。 */
export interface UpmoreCurrency {
  readonly quotaPerUnit: number;
  readonly usdExchangeRate: number;
}

/** 单个连接的用量聚合。error 非空 = 该连接本次查询失败（数据为空）。 */
export interface UpmoreConnUsage {
  readonly connection: UpmoreConnView;
  readonly currency: UpmoreCurrency;
  readonly stat: UpmoreUsageStat | null;
  /** 令牌清单（分页，翻页透传网关）。 */
  readonly tokens: readonly UpmoreToken[];
  readonly tokenPage: number;
  readonly tokenHasMore: boolean;
  /** 调用明细（分页，翻页透传网关）。 */
  readonly recentLogs: readonly UpmoreCallLog[];
  readonly logPage: number;
  readonly logHasMore: boolean;
  readonly error: string | null;
}

/** 用量页一揽子响应：本人全部连接（含各自聚合或错误）。 */
export interface UpmoreUsageOverview {
  /** 缺失表示服务端尚未升级到统一网关契约。 */
  readonly gatewayBaseUrl?: string | null;
  readonly connections: readonly UpmoreConnUsage[];
}

export const api = {
  board: (opts: { hours?: number; all?: boolean } = {}) =>
    get<Board>(`/api/v1/board?hours=${opts.hours ?? 720}${opts.all === true ? '&all=1' : ''}`),
  metrics: () => get<{ metrics: readonly MetricDef[] }>('/api/v1/metrics'),
  trend: (metric: string, periods = 12) =>
    get<Trend>(`/api/v1/trend?metric=${encodeURIComponent(metric)}&periods=${periods}`),
  decisions: () => get<{ decisions: readonly Decision[] }>('/api/v1/decisions'),
  decide: (id: string, choice: string) =>
    post<{ ok: boolean; id: string; chosen: string }>('/api/v1/decisions/decide', { id, choice }),
  adminUsers: () =>
    get<{ users: readonly ManagedUser[]; roles: readonly RoleInfo[] }>('/api/v1/admin/users'),
  adminSetUserRole: (username: string, role: string) =>
    post<{ ok: boolean }>('/api/v1/admin/users/role', { username, role }),
  adminSetUserStatus: (username: string, status: 'active' | 'disabled') =>
    post<{ ok: boolean }>('/api/v1/admin/users/status', { username, status }),
  adminRoles: () =>
    get<{ roles: readonly RoleRow[]; permissions: readonly PermissionDef[] }>(
      '/api/v1/admin/roles',
    ),
  adminCreateRole: (name: string, description: string) =>
    post<{ ok: boolean }>('/api/v1/admin/roles', { name, description }),
  adminSetRolePerms: (role: string, permissions: readonly string[]) =>
    post<{ ok: boolean }>('/api/v1/admin/roles/perms', { role, permissions }),
  adminConfig: () => get<{ items: readonly ConfigItem[] }>('/api/v1/admin/config'),
  adminSetConfig: (key: string, value: unknown, expectedValue?: string) =>
    post<{ ok: boolean }>('/api/v1/admin/config', {
      key,
      value,
      ...(expectedValue !== undefined ? { expectedValue } : {}),
    }),
  tosConfig: () => get<TosConfigView>('/api/v1/admin/tos'),
  attachmentInventory: (input: AttachmentInventoryQueryDto) =>
    get<AttachmentInventory>(
      `/api/v1/admin/attachments?${new URLSearchParams({
        page: String(input.page),
        perPage: String(input.perPage),
        backend: input.backend,
        tab: input.tab,
        q: input.q,
      })}`,
    ),
  attachmentStats: () => get<AttachmentInventoryStats>('/api/v1/admin/attachments/stats'),
  attachmentReferenceStatus: () =>
    get<AttachmentReferenceStatus>('/api/v1/admin/attachments/references'),
  backfillAttachmentReferences: (input: AttachmentBackfillInputDto) =>
    post<AttachmentBackfillResult>('/api/v1/admin/attachments/references/backfill', input),
  setAttachmentReview: (input: AttachmentReviewInputDto) =>
    post<{ ok: boolean }>('/api/v1/admin/attachments/review', input),
  setTosConfig: (input: TosConfigInputDto) => post<TosConfigView>('/api/v1/admin/tos', input),
  testTosConnection: () => post<TosConnectionResult>('/api/v1/admin/tos/test', {}),
  evalModel: () => get<EvalModelView>('/api/v1/admin/eval-model'),
  setEvalModel: (input: { baseUrl: string; model: string; apiKey: string }) =>
    post<{ ok: boolean }>('/api/v1/admin/eval-model', input),
  ssoConfig: () => get<SsoConfigView>('/api/v1/admin/sso'),
  probeCasdoor: (baseUrl: string, clientId: string) =>
    get<{
      ok: boolean;
      reason: string;
      name: string;
      displayName: string;
      organization: string;
    }>(
      `/api/v1/admin/sso/probe-casdoor?baseUrl=${encodeURIComponent(baseUrl)}&clientId=${encodeURIComponent(clientId)}`,
    ),
  setSsoConfig: (input: {
    baseUrl: string;
    clientId: string;
    /** 留空 = 保留已存 secret（编辑场景）；首次配置必填 */
    clientSecret?: string;
    ssoRole: string;
    /** 自绘登录开关：勾选时服务端自动从 Casdoor 识别应用并落库 */
    directLogin: boolean;
  }) => post<{ ok: boolean }>('/api/v1/admin/sso', input),
  ssoStatus: () => get<{ enabled: boolean; directReady: boolean }>('/api/v1/auth/sso/status'),
  ssoStart: () => get<{ url: string }>('/api/v1/auth/sso/start'),
  ssoCallback: (code: string, state: string) =>
    post<AuthResult>('/api/v1/auth/sso/callback', { code, state }),
  /** 自绘 SSO 登录：账密直接换会话（后端代理 Casdoor /api/login type=code）。 */
  ssoDirectLogin: (username: string, password: string) =>
    post<AuthResult>('/api/v1/auth/sso/direct-login', { username, password }),
  /** 浏览器直连模式挑战：拿 state/PKCE 与 Casdoor 连接参数（verifier 留在后端）。 */
  ssoDirectChallenge: () => get<SsoDirectChallenge>('/api/v1/auth/sso/direct-challenge'),
  /**
   * 探测浏览器在 Casdoor 侧的既有会话（其他公司系统登录留下的）。
   * 必须带 cookie 直连 Casdoor —— 本系统域名拿不到也不该拿到它。
   */
  ssoProbeSession: (baseUrl: string) =>
    fetch(`${baseUrl}/api/get-account`, { credentials: 'include' }).then((r) => r.json()),
  /**
   * 浏览器直连拿授权码：已登录会话免密（不带 username），或账密换码。
   * cookie 只在浏览器与 Casdoor 之间流动；code 随后交回 /sso/callback 换发。
   */
  ssoBrowserCode: (
    baseUrl: string,
    challenge: SsoDirectChallenge,
    creds: { username: string; password: string } | null,
  ): Promise<string> =>
    fetch(
      `${baseUrl}/api/login?${new URLSearchParams({
        clientId: challenge.clientId,
        responseType: 'code',
        redirectUri: `${window.location.origin}/sso/callback`,
        scope: 'openid profile',
        state: challenge.state,
        code_challenge_method: 'S256',
        code_challenge: challenge.codeChallenge,
      })}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          type: 'code',
          application: challenge.application,
          organization: challenge.organization,
          ...(creds ?? {}),
          signinMethod: 'Password',
        }),
      },
    ).then(async (r) => {
      const j = (await r.json()) as { status?: string; msg?: string; data?: unknown };
      if (j.status === 'ok' && typeof j.data === 'string' && j.data !== '') return j.data;
      throw new Error(j.msg !== undefined && j.msg !== '' ? j.msg : '统一身份登录失败');
    }),
  claimDesktopHandshake: (handshakeId: string) =>
    post<{ status: 'claimed' }>(
      `/api/v1/auth/desktop-handshake/claim?handshakeId=${encodeURIComponent(handshakeId)}`,
      {},
    ),
  feishuCredentials: () => get<FeishuCredentialsView>('/api/v1/admin/feishu/credentials'),
  adminSetFeishuCredentials: (input: {
    appId?: string;
    appSecret?: string;
    verificationToken?: string;
    encryptKey?: string;
  }) => post<{ ok: boolean }>('/api/v1/admin/feishu/credentials', input),
  attribution: () => get<Attribution>('/api/v1/attribution'),
  hypothesis: (id: string) =>
    get<HypothesisDetail>(`/api/v1/attribution?id=${encodeURIComponent(id)}`),
  tasks: (filter: { status?: string; assignee?: string } = {}) => {
    const p = new URLSearchParams();
    if (filter.status) p.set('status', filter.status);
    if (filter.assignee) p.set('assignee', filter.assignee);
    const qs = p.toString();
    return get<{ tasks: readonly GlobalTask[]; reworkWarnRatio?: number }>(
      `/api/v1/tasks${qs !== '' ? `?${qs}` : ''}`,
    );
  },
  evolution: () => get<Evolution>('/api/v1/evolution'),
  rules: () => get<{ stats: RuleStats; rules: readonly RuleItem[] }>('/api/v1/rules'),
  managedRules: (page: number) => get<RulePage<ManagedRule>>(`/api/v1/rules/managed?page=${page}`),
  ruleHistory: (id: string, page: number) =>
    get<RulePage<RuleExecution>>(
      `/api/v1/rules/history?id=${encodeURIComponent(id)}&page=${page}&perPage=5`,
    ),
  ruleMeta: () => get<RuleMeta>('/api/v1/rules/meta'),
  saveRule: (input: SaveRuleInput) =>
    post<{ ok: boolean; id: string; version: number; status: string }>('/api/v1/rules', {
      ...input,
      status: input.activate === true ? 'active' : 'draft',
    }),
  setRuleStatus: (id: string, status: 'draft' | 'active' | 'disabled', expectedVersion: number) =>
    post<{ ok: boolean; version: number }>('/api/v1/rules/status', { id, status, expectedVersion }),
  harnessHealth: () => get<HarnessHealth>('/api/v1/harness/health'),
  ruleReplay: (input: { from?: string; to?: string; type?: string; limit?: number }) =>
    post<{
      scanned: number;
      matched: number;
      wouldApply: number;
      applied: number;
      escalated: number;
      errors: number;
      perRule: Record<string, number>;
    }>('/api/v1/rules/replay', { ...input }),
  register: (input: { username: string; password: string; displayName?: string }) =>
    post<AuthResult>('/api/v1/auth/register', input),
  login: (input: { username: string; password: string }) =>
    post<AuthResult>('/api/v1/auth/login', input),
  logout: () => post<{ ok: boolean }>('/api/v1/auth/logout', {}),
  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    post<{ ok: boolean }>('/api/v1/auth/change-password', input),
  me: () => get<{ user: AuthUser | null; kind: string }>('/api/v1/me'),
  sessions: () => get<SessionList>('/api/v1/auth/sessions'),
  upmoreUsage: (
    opts: { start?: number; end?: number; tokenPage?: number; logPage?: number } = {},
  ) => {
    const p = new URLSearchParams();
    if (opts.start !== undefined) p.set('start', String(opts.start));
    if (opts.end !== undefined) p.set('end', String(opts.end));
    if (opts.tokenPage !== undefined) p.set('tokenPage', String(opts.tokenPage));
    if (opts.logPage !== undefined) p.set('logPage', String(opts.logPage));
    const qs = p.toString();
    return get<UpmoreUsageOverview>(`/api/v1/usage/upmore${qs !== '' ? `?${qs}` : ''}`);
  },
  upmoreConnections: () =>
    get<{ connections: readonly UpmoreConnView[] }>('/api/v1/usage/upmore/connections'),
  saveUpmoreConnection: (input: {
    label?: string;
    accessToken: string;
    expectedGatewayBaseUrl: string;
  }) =>
    post<{ ok: boolean; connection: UpmoreConnView; identitySync?: string }>(
      '/api/v1/usage/upmore/connections',
      input,
    ),
  deleteUpmoreConnection: (id: string) =>
    post<{ ok: boolean }>('/api/v1/usage/upmore/connections/delete', { id }),
  revokeSession: (tokenHash: string) =>
    post<{ status: 'revoked' }>('/api/v1/auth/sessions/revoke', { tokenHash }),
  loginLog: (limit = 50, offset = 0) =>
    get<LoginLogPage>(`/api/v1/auth/login-log?limit=${limit}&offset=${offset}`),
  // ── GitLab 审计 ──
  gitlabAccounts: () => get<{ accounts: readonly GitlabAccount[] }>('/api/v1/gitlab/accounts'),
  gitlabProjects: (
    accountId: string,
    page = 1,
    perPage = 20,
    search = '',
    includeOwners = false,
  ) => {
    const p = new URLSearchParams({
      accountId,
      page: String(page),
      perPage: String(perPage),
    });
    if (search) p.set('search', search);
    if (includeOwners) p.set('includeOwners', 'true');
    return get<GitlabPage<GitlabProject>>(`/api/v1/gitlab/projects?${p.toString()}`);
  },
  gitlabCommits: (input: {
    accountId: string;
    projectId: number;
    since: string;
    until: string;
    ref?: string;
    author?: string;
    path?: string;
    page?: number;
    perPage?: number;
    includeTotal?: boolean;
  }) => {
    const p = new URLSearchParams({
      accountId: input.accountId,
      projectId: String(input.projectId),
      since: input.since,
      until: input.until,
      page: String(input.page ?? 1),
      perPage: String(input.perPage ?? 20),
    });
    if (input.ref) p.set('ref', input.ref);
    if (input.author) p.set('author', input.author);
    if (input.path) p.set('path', input.path);
    if (input.includeTotal !== undefined) p.set('includeTotal', String(input.includeTotal));
    return get<GitlabPage<GitlabCommit>>(`/api/v1/gitlab/commits?${p.toString()}`);
  },
  gitlabCommit: (accountId: string, projectId: number, sha: string) =>
    get<GitlabCommitDetail>(
      `/api/v1/gitlab/commit?accountId=${encodeURIComponent(accountId)}&projectId=${projectId}&sha=${encodeURIComponent(sha)}`,
    ),
  gitlabCommitStatistics: (input: GitlabAnalyticsQuery) => {
    const p = new URLSearchParams({
      accountId: input.accountId,
      since: input.since,
      until: input.until,
    });
    for (const id of input.projectIds) p.append('projectId', String(id));
    if (input.ref) p.set('ref', input.ref);
    if (input.author) p.set('author', input.author);
    if (input.path) p.set('path', input.path);
    if (input.memberUsername) p.set('memberUsername', input.memberUsername);
    return get<GitlabAnalyticsResult>(`/api/v1/gitlab/analytics/commits?${p.toString()}`);
  },
  // ── GitLab 自助（身份即授权）──
  gitlabSelfAccounts: () =>
    get<{
      accounts: readonly GitlabAccount[];
      hostManaged?: boolean;
      configuredHost?: string | null;
    }>('/api/v1/gitlab/self/accounts'),
  gitlabSelfCredential: () => get<GitlabSelfCredentialView>('/api/v1/gitlab/self/credential'),
  gitlabSaveSelfCredential: (input: { token: string; expectedHost: string }) =>
    post<{ ok: boolean; account: { id: string; username: string | null }; identitySync?: string }>(
      '/api/v1/gitlab/self/credential',
      input,
    ),
  gitlabRevokeSelfCredential: () =>
    post<{ ok: boolean }>('/api/v1/gitlab/self/credential/revoke', {}),
  gitlabSelfCommit: (accountId: string, projectId: number, sha: string) =>
    get<GitlabCommitDetail>(
      `/api/v1/gitlab/self/commit?accountId=${encodeURIComponent(accountId)}&projectId=${projectId}&sha=${encodeURIComponent(sha)}`,
    ),
  gitlabInstanceCredential: () =>
    get<InstanceCredentialView>('/api/v1/admin/gitlab/instance-credential'),
  gitlabSaveInstanceCredential: (input: {
    token: string;
    expectedHost: string;
    expectedAccountId: string | null;
  }) => post<InstanceCredentialView>('/api/v1/admin/gitlab/instance-credential', input),
  gitlabRevokeInstanceCredential: (expectedAccountId: string) =>
    post<{ ok: boolean }>('/api/v1/admin/gitlab/instance-credential/revoke', { expectedAccountId }),
  gitlabIdentities: (search = '', page = 1) =>
    get<{ host: string | null; items: IdentityView[]; nextPage: number | null }>(
      `/api/v1/gitlab/admin/identities?${new URLSearchParams({ search, page: String(page) })}`,
    ),
  gitlabProjectMembers: (projectId: number, page = 1) =>
    get<
      GitlabPage<
        GitlabIdentityCandidate & {
          accessLevel: number | null;
          localUsername: string | null;
          localName: string | null;
        }
      >
    >(
      `/api/v1/gitlab/admin/project-members?${new URLSearchParams({ projectId: String(projectId), page: String(page) })}`,
    ),
  gitlabUsers: (input: Partial<GitlabUsersQuery> = {}) => {
    const params = new URLSearchParams({
      search: input.search ?? '',
      perPage: String(input.perPage ?? 50),
      includeBots: String(input.includeBots ?? true),
    });
    if (input.cursor) params.set('cursor', input.cursor);
    return get<GitlabUsersPage>(`/api/v1/gitlab/admin/users?${params}`);
  },
  gitlabBindIdentity: (input: {
    userId: string;
    gitlabUserId: number;
    expectedHost: string;
    expectedBindingId: string | null;
  }) => post<{ ok: boolean }>('/api/v1/gitlab/admin/identities/bind', input),
  gitlabUnbindIdentity: (input: {
    userId: string;
    expectedHost: string;
    expectedBindingId: string;
  }) => post<{ ok: boolean }>('/api/v1/gitlab/admin/identities/unbind', input),
  gitlabIdentityEmail: (
    action: 'add' | 'remove',
    input: { userId: string; expectedHost: string; expectedBindingId: string; email: string },
  ) => post<{ ok: boolean }>(`/api/v1/gitlab/admin/identities/email/${action}`, input),
  gitlabSelfIdentity: () =>
    get<{ host: string | null; binding: IdentityView | null }>('/api/v1/gitlab/self/identity'),
  gitlabInspectSelfIdentity: () =>
    post<{ host: string; identity: GitlabIdentityCandidate }>(
      '/api/v1/gitlab/self/identity/inspect',
      {},
    ),
  gitlabBindSelfIdentity: (input: {
    gitlabUserId: number;
    expectedHost: string;
    expectedBindingId: string | null;
  }) => post<{ ok: boolean }>('/api/v1/gitlab/self/identity/bind', input),
  gitlabUnbindSelfIdentity: (input: { expectedHost: string; expectedBindingId: string }) =>
    post<{ ok: boolean }>('/api/v1/gitlab/self/identity/unbind', input),
  gitlabSelfEmails: () => post<{ emails: string[] }>('/api/v1/gitlab/self/identity/emails', {}),
  gitlabSelfIdentityEmail: (
    action: 'add' | 'remove',
    input: { expectedHost: string; expectedBindingId: string; email: string },
  ) => post<{ ok: boolean }>(`/api/v1/gitlab/self/identity/email/${action}`, input),
  gitlabSelfProjects: (accountId: string, page = 1, perPage = 20, search = '') => {
    const p = new URLSearchParams({
      accountId,
      page: String(page),
      perPage: String(perPage),
    });
    if (search) p.set('search', search);
    return get<GitlabPage<GitlabProject>>(`/api/v1/gitlab/self/projects?${p.toString()}`);
  },
  gitlabSelfCommits: (input: {
    accountId: string;
    projectId: number;
    since: string;
    until: string;
    ref?: string;
    author?: string;
    path?: string;
    page?: number;
    perPage?: number;
    includeTotal?: boolean;
  }) => {
    const p = new URLSearchParams({
      accountId: input.accountId,
      projectId: String(input.projectId),
      since: input.since,
      until: input.until,
      page: String(input.page ?? 1),
      perPage: String(input.perPage ?? 20),
    });
    if (input.ref) p.set('ref', input.ref);
    if (input.author) p.set('author', input.author);
    if (input.path) p.set('path', input.path);
    if (input.includeTotal !== undefined) p.set('includeTotal', String(input.includeTotal));
    return get<GitlabPage<GitlabCommit>>(`/api/v1/gitlab/self/commits?${p.toString()}`);
  },
};

export function saveSession(r: AuthResult): void {
  localStorage.setItem(TOKEN_KEY, r.token);
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function hasSession(): boolean {
  return token() !== '';
}

// ── 展示 ────────────────────────────────────────────────────

/**
 * 指标值的展示串。
 *
 * **null 显示成「—」而不是 0。** 缺数据与「表现差」是两回事，
 * 显示成 0 会让看板说谎。这条与后端口径一致。
 */
/**
 * 拆分「数值 / 单位」—— 排版上单位应比数字小一档、灰色，不该同字号挤在一起。
 */
export function formatValueParts(v: { value: number | null; unit: string }): {
  num: string;
  unit: string;
} {
  if (v.value === null) return { num: '—', unit: '' };
  if (v.unit === '%') return { num: (v.value * 100).toFixed(1), unit: '%' };
  if (v.unit === '分钟') return { num: v.value.toFixed(0), unit: '分' };
  if (v.unit === '元/件') return { num: v.value.toFixed(2), unit: '元' };
  return { num: v.value.toFixed(2), unit: '' };
}

export function formatValue(v: { value: number | null; unit: string }): string {
  if (v.value === null) return '—';
  if (v.unit === '%') return `${(v.value * 100).toFixed(1)}%`;
  if (v.unit === '分钟') return `${v.value.toFixed(0)} 分`;
  if (v.unit === '元/件') return `${v.value.toFixed(2)} 元`;
  return v.value.toFixed(2);
}

export function directionLabel(d: string): string {
  if (d === 'higher_better') return '越高越好';
  if (d === 'lower_better') return '越低越好';
  return '中性';
}
